"use client"
import { cn } from "@abraxas/ui/lib"
import { Button } from "@abraxas/ui/ui"
import { motion, AnimatePresence } from "motion/react"
import { X } from "lucide-react"
import { useState } from "react"
import type { IGiveaway, VoteData } from "@repo/loomlock/model-types"

const COPY: Record<
  "vote" | "giveaway",
  { title: string; subtitle: string; cta: string }
> = {
  vote: {
    title: "Vote for your favourite design",
    subtitle:
      "and become a pivotal member of the loomlock ecosystem, helping us build this brand together.",
    cta: "Go to vote",
  },
  giveaway: {
    title: "Enter the giveaway",
    subtitle:
      "and get a chance to win exclusive prizes while supporting the loomlock ecosystem.",
    cta: "Go to giveaway",
  },
}

export const HomeCTAUI = ({
  className,
  resource,
  kind,
}: {
  className?: string
  resource: IGiveaway | VoteData
  kind: "vote" | "giveaway"
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const text = COPY[kind]
  const slug = resource?.slug
  const asPath = kind === "vote" ? `/votes/${slug}` : `/giveaways/${slug}`
  const href =
    kind === "vote"
      ? `/votes/[slug]?slug=${slug}`
      : `/giveaways/[slug]?slug=${slug}`

  return (
    <motion.div
      className={cn(
        "!min-w-full md:!min-w-auto",
        "bg-yellow md:rounded-4xl text-brand-dark",
        "overflow-hidden",
        "relative",
        "md:min-h-16 md:min-w-16 px-2 py-8 md:p-8",
        "group",
        !isOpen && "cursor-pointer",
        className,
      )}
      style={{
        transformOrigin: "bottom right",
      }}
      onClick={() => !isOpen && setIsOpen(true)}
      animate={{
        width: isOpen ? 370 : 80,
      }}
      transition={{
        delay: isOpen ? 0 : 0.25,
      }}
    >
      <img
        src="https://lock.nyc3.cdn.digitaloceanspaces.com/assets/home/vote-cta-bg.webp"
        alt="vote"
        className={cn(
          "absolute bottom-0 right-0",
          "delay-150 transition-all",
          isOpen ? "md:w-16 w-24" : "w-12",
        )}
      />
      <AnimatePresence>
        {isOpen && (
          <motion.button
            className="absolute top-5 right-5 z-10 hidden md:block"
            onClick={() => setIsOpen(false)}
            type="button"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1, transition: { delay: 0.3 } }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <X />
          </motion.button>
        )}
        {isOpen && (
          <motion.div
            className="w-[calc(370px-2rem)]"
            initial={{
              opacity: 0,
              y: 10,
              maxHeight: 0,
            }}
            animate={{
              opacity: 1,
              y: 0,
              maxHeight: 500,
              transition: {
                opacity: {
                  delay: 0.25,
                },
                y: {
                  delay: 0.25,
                },
              },
            }}
            exit={{
              opacity: 0,
              y: -10,
              maxHeight: 0,
              transition: {
                maxHeight: {
                  delay: 0.15,
                },
              },
            }}
          >
            <div className="text-subtitle-3 font-bold">{text.title}</div>
            <p className="mt-2 mb-4 max-w-[69%]">{text.subtitle}</p>
            <Button color="brandDark" as={asPath} href={href}>
              {text.cta}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
